import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { unblockUser } from "../../../apis/blocking/blocking-apis";
import {
  BLOCK_REASONS,
  type BlockReason,
  type UnblockUserRequest,
} from "../../../domain/blocking";
import { useShowToast } from "../../../hooks";
import { strings } from "../../../I18n";

interface UnblockUserModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  currentReason?: BlockReason;
  onSuccess: () => void;
}

const UnblockUserModal: React.FC<UnblockUserModalProps> = ({
  open,
  onClose,
  userId,
  userName,
  currentReason,
  onSuccess,
}) => {
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const showToast = useShowToast();

  useEffect(() => {
    if (open) {
      setResolutionNotes("");
    }
  }, [open]);

  const handleUnblock = async () => {
    setLoading(true);
    try {
      const request: UnblockUserRequest = {
        resolutionNotes: resolutionNotes.trim() || undefined,
      };
      await unblockUser(userId, request);
      showToast(strings("blocking.userUnblocked"), "success");
      onSuccess();
      onClose();
    } catch (error: any) {
      showToast(
        error.response?.data?.message || strings("blocking.unblockFailed"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const reasonLabel = currentReason
    ? BLOCK_REASONS.find((r) => r.value === currentReason)?.label
    : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CheckCircleIcon color="success" />
        {strings("blocking.unblockUser")}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
          {strings("blocking.unblockConfirm", { userName })}
        </Alert>

        {reasonLabel && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary">
              {strings("blocking.currentBlockReason")}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={reasonLabel} color="error" size="small" />
            </Box>
          </Box>
        )}

        <TextField
          label={strings("blocking.resolutionNotes")}
          placeholder={strings("blocking.resolutionNotesPlaceholder")}
          fullWidth={true}
          multiline={true}
          rows={3}
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
          helperText={strings("blocking.resolutionNotesHelper")}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {strings("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleUnblock}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <CheckCircleIcon />
            )
          }
        >
          {loading
            ? strings("blocking.unblocking")
            : strings("blocking.unblockUser")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnblockUserModal;
